// Hazır spor kitapları — hangisi yayında, hangi uçtan açılır.
//
// Sağlayıcı çerçevesi **tek sayfadır** (`pages/SportsbookFrame.jsx`);
// sağlayıcıya göre değişen tek şey launch ucudur. Yeni bir hazır sportsbook
// eklerken sayfayı çatallamayın, aşağıdaki eşlemeye bir satır ekleyin.
//
// Karar sunucudadır: `/settings` yanıtındaki `sportsbook_providers` listesi
// dört ayarın **birlikte** sonucudur (servis açık + spor açık + kimlik tam +
// bildirim adresi onaylı). İstemci o listeyi süzmez, yalnızca çizer —
// ayarların kendisi (merchant key, token, secret) ziyaretçiye hiç gelmez.
export const SPORTSBOOK_LAUNCH = {
  betby: '/sportsbook/launch',
  flexrix: '/flexrix-sportsbook/launch',
  emsports: '/emsports-sportsbook/launch',
  sportdigi: '/sportdigi-sportsbook/launch',
};

// Oyuncuya sağlayıcının adı yazılmaz (22.10.2026, sistem sahibi kuralı):
// marka adını yönetici panelden verir, sunucu boş adı nötr ada çevirir
// ("Spor Bahisleri", "Spor Bahisleri 2"). Sunucu ad göndermezse istemci de
// sağlayıcı adı yazmaz. `flexrix` gibi anahtarlar yalnızca teknik kimliktir.
export const NEUTRAL_SPORTSBOOK_LABEL = 'Spor Bahisleri';

// Pakete gömülü varsayılan logolar. Marka panelden yönetilir (aynı yazılım
// farklı markalarla satılır) ama alan boşken şeritte yalnızca sağlayıcının
// adı yazıyordu; kurulumun her birine elle logo yüklemesi gerekiyordu.
// Dosyalar `frontend/public/sportsbook-logos/` altındadır ve adres
// **mutlaktır**: `/api/` ile başlamadığı için `resolveApiAssetUrl` ona
// dokunmaz — logo API'de değil frontend paketindedir ve ayrı origin'li
// kurulumda backend adresine çevrilseydi 404 olurdu.
//
// Anahtarlar hem oyuncuya dönen public id'yi hem panelin marka ekranındaki
// `provider_configs` anahtarını karşılar; ikisi yalnızca Betby'de ayrışır
// (`betby` / `syncode`) ve aynı eşleme ikisine de hizmet eder. Betby'nin
// varsayılanı yoktur; orada eski davranış (sekmede ad yazılır) sürer.
//
// Flexrix'in logosu **BetConstruct**'tır: sağlayıcının SPA'sı BetConstruct
// swarm'ıdır ve oyuncunun gördüğü marka odur.
export const SPORTSBOOK_DEFAULT_LOGOS = {
  flexrix: '/sportsbook-logos/betconstruct.webp',
  emsports: '/sportsbook-logos/everymatrix.svg',
  sportdigi: '/sportsbook-logos/digitain.svg',
};

// Panelden logo girilmemişse çizilecek adres. Yönetici alanı doldurduğunda
// (ya da Yükle ile bir görsel seçtiğinde) varsayılan devreden çıkar.
export const defaultSportsbookLogo = (id) => (
  SPORTSBOOK_DEFAULT_LOGOS[String(id || '')] || ''
);

// Oyuncunun son seçtiği spor kitabı. Tercih **tarayıcıdadır**: bir oyuncunun
// seçimi diğerininkini değiştirmemelidir ve sunucuya yazmak her geçişte bir
// istek demektir. Herkül'ün sohbet rayı ve Vega'nın görünüm anahtarıyla aynı
// yaklaşım.
export const PROVIDER_STORAGE_KEY = 'sb:provider';

// Spor sayfasına dışarıdan (ana sayfanın maç şeridi) hangi spor kitabıyla ve
// hangi sayfasında açılacağını söyleyen adres parametreleri. Seçim adreste
// taşınır, çünkü bağlantı yeni sekmede de açılabilir; sayfa açıldığında
// sağlayıcı tercihi yine tarayıcıya yazılır.
export const SPORTSBOOK_PROVIDER_PARAM = 'sb';
export const SPORTSBOOK_PATH_PARAM = 'sb_path';

// Derin bağlantı yalnızca site içi bir yoldur (backend `normalize_path` ile
// aynı kural): şema taşıyan ya da `//` ile başlayan değer düşürülür.
const safePath = (value) => {
  const path = String(value || '').trim();
  if (!path || path.includes('://') || path.startsWith('//')) return '';
  const normalized = `/${path.replace(/^\/+/, '')}`;
  return normalized.length <= 64 ? normalized : '';
};

export const sportsbookHref = (providerId, path = '') => {
  const params = new URLSearchParams();
  if (providerId) params.set(SPORTSBOOK_PROVIDER_PARAM, providerId);
  const target = safePath(path);
  if (target) params.set(SPORTSBOOK_PATH_PARAM, target);
  const query = params.toString();
  return query ? `/spor?${query}` : '/spor';
};

export const readSportsbookLink = (search) => {
  const params = new URLSearchParams(search || '');
  return {
    provider: String(params.get(SPORTSBOOK_PROVIDER_PARAM) || ''),
    path: safePath(params.get(SPORTSBOOK_PATH_PARAM)),
  };
};

// `/settings` yanıtından çizilebilir listeyi kurar.
//
// Eski istemcilerle uyum için sunucu tek sağlayıcılı `sportsbook_provider`
// alanını da döndürür; buradaki yedek onun tersidir — dağıtım sırasında
// listeyi henüz göndermeyen bir sunucuya düşen yeni paket de spor kitabını
// açabilmelidir.
export const hostedSportsbooks = (settings) => {
  const rows = Array.isArray(settings?.sportsbook_providers)
    ? settings.sportsbook_providers
    : [];
  const list = rows.length || !settings?.sportsbook_provider
    ? rows
    : [{ id: settings.sportsbook_provider }];
  return list
    .map((row) => ({
      id: String(row?.id || ''),
      label: String(row?.label || '').trim()
        || NEUTRAL_SPORTSBOOK_LABEL,
      // Paketteki sağlayıcı logosu yalnız panelde çizilir; oyuncuya yönetici
      // logosu yüklemediyse logo yerine nötr ad yazılır (22.10.2026).
      logo: String(row?.logo || '').trim(),
      endpoint: SPORTSBOOK_LAUNCH[row?.id] || '',
    }))
    // Tanımadığımız bir sağlayıcı elenir: uçsuz bir sekme çizmek oyuncuya
    // hiç açılmayacak bir düğme vermektir.
    .filter((row) => row.endpoint);
};

// Oyuncunun seçimi hâlâ geçerli mi. Kurulum sağlayıcıyı panelden kapatmış
// olabilir; kayıtlı seçim o zaman sessizce listenin ilkine düşer — aksi
// hâlde spor sayfası boş açılırdı.
export const resolveProvider = (providers, wanted) => {
  const found = providers.find((row) => row.id === wanted);
  return found || providers[0] || null;
};

export default hostedSportsbooks;
